/**
 * OperationalHeatmap v4 — Analytical Decision Panel
 *
 * Features:
 *  - Utilization-based density coloring (not count-based)
 *  - Temporal animation (play/pause timeline)
 *  - Simple load forecast (dashed cells)
 *  - SVG load line overlay
 *  - Automatic bottleneck detection + label
 *  - Trend indicator (↑ ↓ →)
 *  - Professional tooltip (utilization %, status)
 *  - Comparison mode architecture (dataBaseline / dataSimulated)
 */

import React, { useState, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle, Clock, Flame, TrendingUp, TrendingDown,
  Minus, Users, X, Zap,
} from 'lucide-react';
import {
  getHeatmapMatrix,
  DAY_LABELS,
  type HeatmapFilters,
  type HeatmapCell,
  type DayIndex,
  type HeatmapTaskRow,
  type CapacityConfig,
} from '../services/heatmapService';
import {
  enrichCells,
  detectBottlenecks,
  computeForecast,
  computeTrend,
  computeLoadLine,
  getUtilizationColor,
  getGapOverlayColor,
  getHeatmapTextColor,
  type EnrichedCell,
  type ForecastCell,
  type Trend,
} from '../services/heatmapEngine';

// ─── Types ────────────────────────────────────────────────────────────────────

interface OperationalHeatmapProps {
  organizationId: string;
  startDate: string;
  endDate: string;
  capacityConfig?: CapacityConfig;
  /** Multiply displayed cell load by this factor (simulation mode). Does not refetch. */
  simulationMultiplier?: number;
  className?: string;
}

type ViewMode = 'today' | 'workweek' | 'fullweek';

const WORK_DAYS: DayIndex[] = [1, 2, 3, 4, 5];
const FULL_WEEK: DayIndex[] = [0, 1, 2, 3, 4, 5, 6];
const WORK_HOURS: number[] = Array.from({ length: 15 }, (_, i) => i + 6);
const ALL_HOURS: number[]  = Array.from({ length: 24 }, (_, i) => i);
const CELL_H = 32;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtHour(h: number): string {
  return `${h.toString().padStart(2, '0')}h`;
}

function fmtMin(min: number | null): string {
  if (min === null) return '—';
  if (min < 60) return `${Math.round(min)}m`;
  return `${Math.floor(min / 60)}h ${Math.round(min % 60)}m`;
}

function statusLabel(u: number): string {
  if (u === 0)  return '—';
  if (u < 0.8)  return 'OK';
  if (u < 1.0)  return 'Atenção';
  return 'Gargalo';
}

function statusColor(u: number): string {
  if (u < 0.8)  return '#34D399';
  if (u < 1.0)  return '#FBBF24';
  return '#F87171';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function InlineMetric({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[9px] text-[var(--text-muted)]">{label}:</span>
      <span className="text-[10px] font-semibold tabular-nums" style={{ color: color ?? 'var(--text-primary)' }}>
        {value}
      </span>
    </div>
  );
}

function LegendItem({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      <div
        className="rounded-[2px]"
        style={{
          width: 18, height: 10,
          backgroundColor: color,
          border: dashed ? '1px dashed var(--border-default)' : '1px solid var(--border-default)',
          opacity: dashed ? 0.6 : 1,
        }}
      />
      <span className="text-[9px] text-[var(--text-muted)]">{label}</span>
    </div>
  );
}

// ─── SVG Load Line ────────────────────────────────────────────────────────────

function LoadLineOverlay({
  loadLine, hours, cellWidth,
}: {
  loadLine: { hour: number; utilization: number }[];
  hours: number[];
  cellWidth: number;
}) {
  if (loadLine.every(p => p.utilization === 0)) return null;
  const maxUtil = Math.max(...loadLine.map(p => p.utilization), 1);
  const svgH = CELL_H;
  const dayLabelW = 36;
  const points = loadLine.map((p, i) => {
    const x = dayLabelW + i * cellWidth + cellWidth / 2;
    const y = svgH - (p.utilization / maxUtil) * (svgH - 4) - 2;
    return `${x},${y}`;
  });
  return (
    <svg
      className="pointer-events-none absolute inset-0"
      style={{ width: '100%', height: svgH, zIndex: 3 }}
    >
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke="rgba(255,255,255,0.55)"
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        strokeDasharray="3 2"
      />
      {loadLine.map((p, i) => p.utilization > 0 && (
        <circle
          key={i}
          cx={dayLabelW + i * cellWidth + cellWidth / 2}
          cy={svgH - (p.utilization / maxUtil) * (svgH - 4) - 2}
          r={2}
          fill="rgba(255,255,255,0.7)"
        />
      ))}
    </svg>
  );
}

// ─── Trend badge ──────────────────────────────────────────────────────────────

function TrendBadge({ trend }: { trend: Trend }) {
  if (trend === 'up')   return <span className="flex items-center gap-0.5 text-[9px] font-bold text-red-400"><TrendingUp size={10} /> Alta</span>;
  if (trend === 'down') return <span className="flex items-center gap-0.5 text-[9px] font-bold text-emerald-400"><TrendingDown size={10} /> Queda</span>;
  return <span className="flex items-center gap-0.5 text-[9px] font-medium text-[var(--text-muted)]"><Minus size={10} /> Estável</span>;
}

// ─── Drill-down modal ─────────────────────────────────────────────────────────

function DrillDownModal({
  cell, enriched, rows, capacityConfig, onClose,
}: {
  cell: HeatmapCell;
  enriched: EnrichedCell | undefined;
  rows: HeatmapTaskRow[];
  capacityConfig?: CapacityConfig;
  onClose: () => void;
}) {
  const tasks = useMemo(() => rows.filter(r => cell.taskIds.includes(r.id)), [rows, cell.taskIds]);
  const util = enriched?.utilization ?? 0;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg overflow-hidden rounded-[6px] border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[var(--border-default)] px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="text-[12px] font-semibold text-[var(--text-primary)]">
              {DAY_LABELS[cell.day]} · {fmtHour(cell.hour)}–{fmtHour(cell.hour + 1)}
            </span>
            <span className="rounded-[3px] bg-[var(--bg-elevated)] px-2 py-0.5 text-[10px] font-medium text-[var(--text-secondary)]">
              {tasks.length} execuç{tasks.length === 1 ? 'ão' : 'ões'}
            </span>
            {util > 0 && (
              <span className="text-[10px] font-bold" style={{ color: statusColor(util) }}>
                {statusLabel(util)} · {Math.round(util * 100)}%
              </span>
            )}
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-[var(--button-hover)]">
            <X size={13} className="text-[var(--text-secondary)]" />
          </button>
        </div>

        {util > 0 && (
          <div className="border-b border-[var(--border-default)] px-4 py-2">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1.5 overflow-hidden rounded-full bg-[var(--bg-app)]">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(100, Math.round(util * 100))}%`, backgroundColor: statusColor(util) }}
                />
              </div>
              <span className="text-[10px] font-semibold tabular-nums" style={{ color: statusColor(util) }}>
                {Math.round(util * 100)}% ocupação
              </span>
            </div>
            {enriched && (
              <div className="mt-1.5 flex items-center gap-4">
                <InlineMetric label="Tarefas" value={String(enriched.count)} />
                {capacityConfig && <InlineMetric label="Capacidade" value={`${capacityConfig.totalCapacityPerHour.toFixed(1)}/h`} />}
                {enriched.estimatedMinutes > 0 && <InlineMetric label="Carga est." value={fmtMin(enriched.estimatedMinutes)} />}
                {enriched.avgQueueMinutes != null && (
                  <InlineMetric label="Fila média" value={fmtMin(enriched.avgQueueMinutes)} color={enriched.avgQueueMinutes > 60 ? '#FBBF24' : undefined} />
                )}
              </div>
            )}
          </div>
        )}

        {util >= 1.0 && capacityConfig?.topRoleLabel && (
          <div className="flex items-start gap-2.5 border-b border-[var(--border-default)] bg-red-500/8 px-4 py-2.5">
            <Users size={12} className="mt-0.5 shrink-0 text-red-400" />
            <p className="text-[10px] leading-relaxed text-red-300">
              <strong>Gargalo:</strong> ocupação em {Math.round(util * 100)}% da capacidade de{' '}
              <strong>{capacityConfig.topRoleLabel}</strong>. Redistribua ou aumente FTE.
            </p>
          </div>
        )}
        {util >= 0.8 && util < 1.0 && (
          <div className="flex items-start gap-2.5 border-b border-[var(--border-default)] bg-amber-500/8 px-4 py-2.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0 text-amber-400" />
            <p className="text-[10px] leading-relaxed text-amber-400">
              <strong>Atenção:</strong> próximo do limite de capacidade ({Math.round(util * 100)}%).
            </p>
          </div>
        )}

        <div className="max-h-56 divide-y divide-[var(--border-default)] overflow-y-auto">
          {tasks.length === 0 ? (
            <p className="py-6 text-center text-[11px] text-[var(--text-muted)]">Nenhuma execução encontrada</p>
          ) : tasks.map(t => (
            <div key={t.id} className="flex items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="truncate text-[11px] font-medium text-[var(--text-primary)]">
                  {t.stage_name ?? 'Etapa desconhecida'}
                </div>
                <div className="mt-0.5 flex items-center gap-3 text-[9px] text-[var(--text-muted)]">
                  {t.assigned_to && <span>Resp: {t.assigned_to.slice(0, 12)}</span>}
                  {t.queue_duration != null && <span>Fila: {fmtMin(t.queue_duration)}</span>}
                  {t.planned_duration != null && <span>Plano: {t.planned_duration}m</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export const OperationalHeatmap: React.FC<OperationalHeatmapProps> = ({
  organizationId, startDate, endDate, capacityConfig, simulationMultiplier, className,
}) => {
  const { isDark } = useTheme();
  const [viewMode,     setViewMode]     = useState<ViewMode>('today');
  const [hoveredKey,   setHoveredKey]   = useState<string | null>(null);
  const [clickedCell,  setClickedCell]  = useState<HeatmapCell | null>(null);
  const [displayMode,  setDisplayMode]  = useState<'density' | 'stage'>('density');

  const filters: HeatmapFilters = { organizationId, startDate, endDate };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['heatmap-v2', organizationId, startDate, endDate],
    queryFn: () => getHeatmapMatrix(filters),
    staleTime: 5 * 60 * 1000,
  });

  const todayDayIndex = useMemo(() => new Date().getDay() as DayIndex, []);

  const days = useMemo((): DayIndex[] => {
    if (viewMode === 'today')    return [todayDayIndex];
    if (viewMode === 'workweek') return WORK_DAYS;
    return FULL_WEEK;
  }, [viewMode, todayDayIndex]);

  const hours = viewMode === 'fullweek' ? ALL_HOURS : WORK_HOURS;
  const cellWidth = viewMode === 'fullweek' ? 28 : 40;

  // ── Enriched cells ─────────────────────────────────────────────────────────

  const simMult = simulationMultiplier ?? 1;

  const enrichedMap = useMemo(
    () => enrichCells(data?.cells ?? [], capacityConfig, simMult),
    [data?.cells, capacityConfig, simMult],
  );

  const primaryDay = days[0];

  const bottlenecks = useMemo(
    () => detectBottlenecks(enrichedMap, hours, primaryDay),
    [enrichedMap, hours, primaryDay],
  );

  const forecasts = useMemo(
    () => computeForecast(enrichedMap, hours, primaryDay),
    [enrichedMap, hours, primaryDay],
  );

  const forecastMap = useMemo(() => {
    const m = new Map<string, ForecastCell>();
    for (const f of forecasts) m.set(`${f.day}-${f.hour}`, f);
    return m;
  }, [forecasts]);

  const trend = useMemo(
    () => computeTrend(enrichedMap, hours, primaryDay),
    [enrichedMap, hours, primaryDay],
  );

  const loadLine = useMemo(
    () => computeLoadLine(enrichedMap, hours, primaryDay),
    [enrichedMap, hours, primaryDay],
  );

  const peakKey = useMemo(() => {
    let maxUtil = 0, key: string | null = null;
    for (const [k, cell] of enrichedMap) {
      if (cell.utilization > maxUtil) { maxUtil = cell.utilization; key = k; }
    }
    return key;
  }, [enrichedMap]);

  const peakInfo = useMemo(() => {
    if (!peakKey) return null;
    const cell = enrichedMap.get(peakKey);
    if (!cell) return null;
    const parts = peakKey.split('-');
    return { day: Number(parts[0]) as DayIndex, hour: Number(parts[1]), utilization: cell.utilization };
  }, [peakKey, enrichedMap]);

  // ── Smart insight ──────────────────────────────────────────────────────────

  const smartInsight = useMemo(() => {
    if (!data) return null;
    if (bottlenecks.length > 0) {
      const worst = [...bottlenecks].sort((a, b) => b.peakUtilization - a.peakUtilization)[0];
      return {
        type: 'overload' as const,
        text: `Gargalo ${fmtHour(worst.startHour)}–${fmtHour(worst.endHour)} (${DAY_LABELS[worst.day]}) · ${Math.round(worst.peakUtilization * 100)}% ocupação`,
        recommendation: `Aumentar +1 FTE no pico (${DAY_LABELS[worst.day]} ${fmtHour(worst.startHour)})`,
      };
    }
    if (capacityConfig) {
      return { type: 'stable' as const, text: 'Operação dentro da capacidade no período' };
    }
    if (data.peakSlot) {
      const { day, hour, count, dominantStage } = data.peakSlot;
      const stage = dominantStage ? ` · "${dominantStage}"` : '';
      return { type: 'peak' as const, text: `Pico: ${DAY_LABELS[day]} ${fmtHour(hour)}–${fmtHour(hour + 1)}${stage} · ${count} tarefa${count !== 1 ? 's' : ''} simultâneas` };
    }
    return data.insight ? { type: 'info' as const, text: data.insight } : null;
  }, [data, bottlenecks, capacityConfig]);

  const hoveredEnriched = hoveredKey ? enrichedMap.get(hoveredKey) : undefined;
  const isEmpty         = !data || data.totalTasks === 0;
  const isGapMode       = !!capacityConfig;

  // ── Stage matrix (for displayMode === 'stage') ──────────────────────────

  const stageMatrix = useMemo(() => {
    if (!data?.rows || data.rows.length === 0) return null;
    const stageMap = new Map<string, Map<number, number>>(); // stage → hour → count
    for (const row of data.rows) {
      if (!row.started_at) continue;
      const stageName = row.stage_name ?? '(sem etapa)';
      const startTs = new Date(row.started_at);
      const endTs = row.completed_at
        ? new Date(row.completed_at)
        : new Date(startTs.getTime() + 60 * 60 * 1000);
      const cursor = new Date(startTs);
      cursor.setMinutes(0, 0, 0);
      let steps = 0;
      while (cursor <= endTs && steps < 48) {
        steps++;
        const h = cursor.getHours();
        if (!stageMap.has(stageName)) stageMap.set(stageName, new Map());
        const hm = stageMap.get(stageName)!;
        hm.set(h, (hm.get(h) ?? 0) + 1);
        cursor.setHours(cursor.getHours() + 1);
      }
    }
    if (stageMap.size === 0) return null;
    const stageNames = [...stageMap.keys()].sort();
    const maxCount = Math.max(1, ...stageNames.flatMap(s => [...stageMap.get(s)!.values()]));
    return { stageNames, stageMap, maxCount };
  }, [data?.rows]);

  return (
    <div
      className={`flex flex-col overflow-hidden rounded-[5px] border border-[var(--border-default)] bg-[var(--bg-surface)] ${className ?? ''}`}
      style={{ minHeight: 260 }}
    >
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4 border-b border-[var(--border-default)] px-5 py-3">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <TrendingUp size={12} strokeWidth={2} className="text-[var(--text-secondary)]" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--text-primary)]">
              Heatmap de Carga
            </span>
            {isGapMode && (
              <span className="inline-flex items-center rounded-[3px] bg-[#2F6FED]/18 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-[#60A5FA]">
                GAP
              </span>
            )}
            {displayMode === 'stage' && (
              <span className="inline-flex items-center rounded-[3px] bg-violet-500/18 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-violet-400">
                Por etapa
              </span>
            )}
            {!isEmpty && <TrendBadge trend={trend} />}
          </div>
          {!isEmpty && data && (
            <span className="text-[10px] text-[var(--text-secondary)]">
              {data.totalTasks} tarefa{data.totalTasks !== 1 ? 's' : ''}
              {peakInfo && <> · Pico: {DAY_LABELS[peakInfo.day]} {fmtHour(peakInfo.hour)} ({Math.round(peakInfo.utilization * 100)}%)</>}
              {isGapMode && capacityConfig && <> · Cap: {capacityConfig.totalCapacityPerHour.toFixed(1)}/h</>}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isEmpty && (
            <button
              type="button"
              onClick={() => setDisplayMode(v => v === 'stage' ? 'density' : 'stage')}
              title="Alternar visualização por etapa"
              className={`rounded-[3px] border px-2 py-1 text-[9px] font-semibold uppercase tracking-wide transition-colors ${
                displayMode === 'stage'
                  ? 'border-violet-500/60 bg-violet-500/15 text-violet-400'
                  : 'border-[var(--border-default)] text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              }`}
            >
              Linha
            </button>
          )}
          <div className="inline-flex overflow-hidden rounded-[3px] border border-[var(--border-default)]">
            {([['today', 'Hoje'], ['workweek', 'Seg–Sex'], ['fullweek', 'Semana']] as [ViewMode, string][]).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1 text-[10px] font-semibold uppercase tracking-wide transition-colors duration-100 ${
                  viewMode === mode ? 'bg-[#2F6FED] text-white' : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:bg-[var(--button-hover)]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Insight banner ──────────────────────────────────── */}
      {!isLoading && !isEmpty && smartInsight && (() => {
        const isOverload = smartInsight.type === 'overload';
        const isStable   = smartInsight.type === 'stable';
        const isPeak     = smartInsight.type === 'peak';
        return (
          <div className={`flex items-center gap-2.5 px-5 py-2 ${
            isOverload ? 'border-l-[3px] border-l-red-500 bg-red-500/6' :
            isStable   ? 'border-l-[3px] border-l-emerald-500 bg-emerald-500/6' :
            isPeak     ? 'border-l-[3px] border-l-amber-400 bg-amber-400/6' : ''
          }`}>
            {isOverload && <AlertTriangle size={11} className="shrink-0 text-red-400" />}
            {isPeak     && <Zap size={11} className="shrink-0 text-amber-400" />}
            {isStable   && <span className="text-[11px] text-emerald-400">✔</span>}
            <div className="flex flex-1 flex-col gap-0.5">
              <span className={`text-[10px] font-medium leading-snug ${
                isOverload ? 'text-red-400' : isStable ? 'text-emerald-400' : isPeak ? 'text-amber-400' : 'text-[var(--text-secondary)]'
              }`}>
                {smartInsight.text}
              </span>
              {'recommendation' in smartInsight && smartInsight.recommendation && (
                <span className="text-[9px] text-amber-400 font-semibold">
                  ↗ Recomendação: {(smartInsight as { recommendation: string }).recommendation}
                </span>
              )}
            </div>
            {bottlenecks.length > 1 && (
              <span className="shrink-0 text-[9px] font-semibold text-red-400">
                {bottlenecks.length} janelas
              </span>
            )}
          </div>
        );
      })()}

      {/* ── Loading ─────────────────────────────────────────── */}
      {isLoading && (
        <div className="flex flex-1 items-center justify-center py-12">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--border-default)] border-t-[#2F6FED]" />
        </div>
      )}

      {/* ── Error ───────────────────────────────────────────── */}
      {isError && (
        <div className="flex flex-1 items-center justify-center gap-2 py-8 text-[11px] text-red-400">
          <AlertTriangle size={13} /> Erro ao carregar heatmap
        </div>
      )}

      {/* ── Grid ────────────────────────────────────────────── */}
      {!isLoading && !isError && (
        <div className="flex-1 overflow-x-auto overflow-y-hidden px-5 pb-4 pt-3">
          {isEmpty ? (
            <div className="flex h-32 flex-col items-center justify-center gap-1.5">
              <span className="text-[12px] font-medium text-[var(--text-secondary)]">Sem volume no período</span>
              <span className="text-[10px] text-[var(--text-muted)]">Nenhuma execução iniciada no intervalo selecionado</span>
            </div>
          ) : displayMode === 'stage' && stageMatrix ? (
            // ── Stage × Hour grid ──────────────────────────────
            <table className="border-collapse" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 100 }} />
                  {hours.map(h => (
                    <th
                      key={h}
                      className="pb-1.5 pt-0 text-center font-mono text-[8px] font-semibold text-[var(--text-secondary)]"
                      style={{ width: cellWidth }}
                    >
                      {h % 2 === 0 ? fmtHour(h) : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stageMatrix.stageNames.map(stageName => (
                  <tr key={stageName}>
                    <td
                      className="pr-2.5 text-right text-[9px] font-semibold text-[var(--text-primary)] truncate"
                      style={{ lineHeight: `${CELL_H}px`, maxWidth: 100 }}
                      title={stageName}
                    >
                      {stageName.length > 14 ? stageName.slice(0, 13) + '…' : stageName}
                    </td>
                    {hours.map(h => {
                      const count = stageMatrix.stageMap.get(stageName)?.get(h) ?? 0;
                      const util  = count / stageMatrix.maxCount;
                      const key   = `stage-${stageName}-${h}`;
                      const isHov = key === hoveredKey;
                      const bg    = count > 0 ? getUtilizationColor(util, isDark) : 'var(--bg-elevated)';
                      return (
                        <td
                          key={h}
                          onMouseEnter={() => setHoveredKey(key)}
                          onMouseLeave={() => setHoveredKey(null)}
                          style={{
                            height: CELL_H,
                            position: 'relative',
                            backgroundColor: bg,
                            border: '1px solid var(--border-default)',
                            boxShadow: isHov && count > 0 ? '0 4px 12px rgba(0,0,0,0.35)' : 'none',
                            transform: isHov && count > 0 ? 'scale(1.06)' : 'scale(1)',
                            filter:    isHov && count > 0 ? 'brightness(1.12)' : 'brightness(1)',
                            zIndex: isHov ? 2 : 1,
                            transition: 'all 0.12s ease',
                          }}
                        >
                          {count > 0 && (
                            <div style={{
                              position: 'absolute', inset: 0,
                              display: 'flex', flexDirection: 'column',
                              alignItems: 'center', justifyContent: 'center',
                              color: getHeatmapTextColor(util, isDark),
                              pointerEvents: 'none',
                            }}>
                              <span style={{ fontSize: count >= 10 ? 8 : 11, fontWeight: 700, fontFamily: 'ui-monospace, monospace', letterSpacing: '-0.02em', lineHeight: 1 }}>
                                {count}
                              </span>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : displayMode === 'stage' ? (
            // Fallback: no stage data
            <div className="flex h-32 flex-col items-center justify-center gap-1.5">
              <span className="text-[12px] font-medium text-[var(--text-secondary)]">Sem dados de etapa</span>
              <span className="text-[10px] text-[var(--text-muted)]">As tarefas do período não possuem etapa registrada</span>
            </div>
          ) : (
            <table className="border-collapse" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 36 }} />
                  {hours.map(h => (
                    <th
                      key={h}
                      className="pb-1.5 pt-0 text-center font-mono text-[8px] font-semibold text-[var(--text-secondary)]"
                      style={{ width: cellWidth }}
                    >
                      {h % 2 === 0 ? fmtHour(h) : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {days.map(dayIdx => {
                  const showLineForRow = viewMode === 'today' && dayIdx === primaryDay;
                  return (
                    <tr key={dayIdx}>
                      <td
                        className="pr-2.5 text-right text-[10px] font-bold text-[var(--text-primary)]"
                        style={{ lineHeight: `${CELL_H}px` }}
                      >
                        {DAY_LABELS[dayIdx]}
                      </td>
                      {hours.map((h, hIdx) => {
                        const key        = `${dayIdx}-${h}`;
                        const enriched   = enrichedMap.get(key);
                        const forecast   = forecastMap.get(key);
                        const util       = enriched?.utilization ?? forecast?.utilization ?? 0;
                        const count      = enriched?.count ?? forecast?.count ?? 0;
                        const isFcast    = !enriched && !!forecast;
                        const isPeakCell = key === peakKey && !isFcast;
                        const isHover    = key === hoveredKey;
                        const hasData    = count > 0;
                        const isOverload = util >= 1.0 && hasData && !isFcast;

                        const bg = isFcast
                          ? getUtilizationColor(forecast!.utilization, isDark)
                          : isGapMode
                          ? (hasData ? getGapOverlayColor(util, isDark) : 'var(--bg-elevated)')
                          : getUtilizationColor(util, isDark);

                        return (
                          <td
                            key={h}
                            onMouseEnter={() => setHoveredKey(key)}
                            onMouseLeave={() => setHoveredKey(null)}
                            onClick={() => enriched && hasData ? setClickedCell(enriched) : undefined}
                            style={{
                              height: CELL_H,
                              position: 'relative',
                              backgroundColor: bg,
                              opacity: isFcast ? 0.45 : 1,
                              border: isOverload
                                ? '1px solid #DC2626'
                                : isFcast
                                ? '1px dashed rgba(255,255,255,0.35)'
                                : '1px solid var(--border-default)',
                              outline: isPeakCell && hasData ? '2px solid #F59E0B' : 'none',
                              outlineOffset: '-2px',
                              boxShadow: isOverload
                                ? '0 0 0 1px rgba(220,38,38,0.6), 0 0 10px rgba(239,68,68,0.18)'
                                : isHover && hasData
                                ? '0 4px 12px rgba(0,0,0,0.35)'
                                : 'none',
                              transform:  isHover && hasData && !isFcast ? 'scale(1.06)' : 'scale(1)',
                              filter:     isHover && hasData ? 'brightness(1.12)' : 'brightness(1)',
                              zIndex:     isHover ? 2 : 1,
                              transition: 'all 0.12s ease',
                              cursor:     hasData && !isFcast ? 'pointer' : 'default',
                            }}
                          >
                            {showLineForRow && hIdx === 0 && (
                              <LoadLineOverlay loadLine={loadLine} hours={hours} cellWidth={cellWidth} />
                            )}
                            {hasData && (
                              <div style={{
                                position: 'absolute', inset: 0,
                                display: 'flex', flexDirection: 'column',
                                alignItems: 'center', justifyContent: 'center',
                                gap: 0,
                                color: getHeatmapTextColor(util, isDark),
                                pointerEvents: 'none',
                              }}>
                                <span style={{ fontSize: count >= 10 ? 8 : 11, fontWeight: 700, fontFamily: 'ui-monospace, monospace', letterSpacing: '-0.02em', lineHeight: 1 }}>
                                  {isFcast ? '~' : ''}{count}
                                </span>
                                {capacityConfig && util > 0 && (
                                  <span style={{ fontSize: 8, opacity: 0.75, lineHeight: 1, marginTop: 1 }}>
                                    {Math.round(util * 100)}%
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Tooltip bar ─────────────────────────────────────── */}
      {hoveredEnriched && hoveredEnriched.count > 0 && (
        <div className="border-t border-[var(--border-default)] bg-[var(--bg-elevated)] px-5 py-2">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <div className="flex items-center gap-1.5">
              <Clock size={10} className="text-[var(--text-muted)]" />
              <span className="text-[11px] font-semibold text-[var(--text-primary)]">
                {DAY_LABELS[hoveredEnriched.day]} · {fmtHour(hoveredEnriched.hour)}–{fmtHour(hoveredEnriched.hour + 1)}
              </span>
            </div>
            <InlineMetric label="Tarefas" value={String(hoveredEnriched.count)} />
            <InlineMetric
              label="Ocupação"
              value={`${Math.round(hoveredEnriched.utilization * 100)}%`}
              color={statusColor(hoveredEnriched.utilization)}
            />
            <InlineMetric
              label="Status"
              value={statusLabel(hoveredEnriched.utilization)}
              color={statusColor(hoveredEnriched.utilization)}
            />
            {capacityConfig && <InlineMetric label="Cap." value={`${capacityConfig.totalCapacityPerHour.toFixed(1)}/h`} />}
            {hoveredEnriched.estimatedMinutes > 0 && <InlineMetric label="Carga est." value={fmtMin(hoveredEnriched.estimatedMinutes)} />}
            {hoveredEnriched.dominantStage && <InlineMetric label="Etapa" value={hoveredEnriched.dominantStage} />}
            {hoveredEnriched.topAssignees.length > 0 && (
              <InlineMetric label="Resp." value={hoveredEnriched.topAssignees.slice(0, 2).map(a => a.slice(0, 8)).join(', ')} />
            )}
            <span className="text-[9px] italic text-[var(--text-muted)]">↑ clique p/ detalhes</span>
          </div>
        </div>
      )}

      {/* ── Legend ──────────────────────────────────────────── */}
      {!isLoading && !isError && !isEmpty && (
        <div className="flex items-center justify-between gap-4 border-t border-[var(--border-default)] px-5 py-2">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[8px] font-semibold uppercase tracking-[0.12em] text-[var(--text-muted)]">
              Ocupação
            </span>
            <LegendItem color={getUtilizationColor(0.25, isDark)} label="<50%" />
            <LegendItem color={getUtilizationColor(0.65, isDark)} label="50–80%" />
            <LegendItem color={getUtilizationColor(0.90, isDark)} label="80–100%" />
            <LegendItem color={getUtilizationColor(1.00, isDark)} label=">100%" />
            {forecasts.length > 0 && <LegendItem color={getUtilizationColor(0.65, isDark)} label="previsão" dashed />}
          </div>
          {bottlenecks.length > 0 && (
            <span className="shrink-0 text-[9px] font-semibold text-red-400">
              <Flame size={9} className="inline mr-1" />
              Maior carga: {fmtHour(bottlenecks[0].startHour)} ({Math.round(bottlenecks[0].peakUtilization * 100)}% cap.)
            </span>
          )}
        </div>
      )}

      {/* ── Drill-down modal ─────────────────────────────────── */}
      {clickedCell && data && (
        <DrillDownModal
          cell={clickedCell}
          enriched={enrichedMap.get(`${clickedCell.day}-${clickedCell.hour}`)}
          rows={data.rows}
          capacityConfig={capacityConfig}
          onClose={() => setClickedCell(null)}
        />
      )}
    </div>
  );
};

export default OperationalHeatmap;

